import axios from 'axios'
import { env } from '@/config/env'
import type { TenantPublicEInvoicePolicy } from '@/services/api/tenant-public-settings-api'

/**
 * Public tenant branding JSON (strings plus supportedLocales array).
 * Other keys may exist for forward compatibility.
 */
export type TenantBrandingRecord = Record<string, string | string[] | undefined> & {
  supportedLocales?: string[]
}

export function tenantBrandingString(record: TenantBrandingRecord, key: string): string {
  const v = record[key]
  return typeof v === 'string' ? v : ''
}

export type PublicTenantBrandingPayload = {
  branding: TenantBrandingRecord
  brandingRevision: number
  cpEntitlements?: string[]
  cpPurchasedAddOns?: string[]
  cpDisabledAddOns?: string[]
  seatingProActive?: boolean
  giftCardsActive?: boolean
  vendorSystemActive?: boolean
  mobileAppActive?: boolean
  analyticsActive?: boolean
  aiWebshopAssistantActive?: boolean
  hardTicketsActive?: boolean
  marketingSuiteActive?: boolean
  experienceProviderActive?: boolean
  eInvoicePolicy?: TenantPublicEInvoicePolicy | null
}

function activeAddOn(cpEntitlements: string[], cpDisabledAddOns: string[], code: string): boolean {
  return cpEntitlements.includes(`addon:${code}`) && !cpDisabledAddOns.includes(code)
}

function asPublicEInvoicePolicy(value: unknown): TenantPublicEInvoicePolicy | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }
  return value as TenantPublicEInvoicePolicy
}

/**
 * Fetches the public branding snapshot (no auth), including monotonic revision for cache/offline checks.
 */
export async function fetchPublicTenantBranding(): Promise<PublicTenantBrandingPayload> {
  const res = await axios.get<{
    branding: TenantBrandingRecord
    brandingRevision?: number
    cpEntitlements?: unknown
    cpPurchasedAddOns?: unknown
    cpDisabledAddOns?: unknown
    seatingProActive?: unknown
    giftCardsActive?: unknown
    vendorSystemActive?: unknown
    mobileAppActive?: unknown
    analyticsActive?: unknown
    aiWebshopAssistantActive?: unknown
    hardTicketsActive?: unknown
    marketingSuiteActive?: unknown
    experienceProviderActive?: unknown
    eInvoicePolicy?: unknown
  }>(`${env.apiUrl}/api/v1/public/tenant-branding`, {
    timeout: 15000,
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
  })
  const b = res.data?.branding
  if (!b || typeof b !== 'object' || Array.isArray(b)) {
    return {
      branding: {},
      brandingRevision: 0,
      cpEntitlements: [],
      cpPurchasedAddOns: [],
      cpDisabledAddOns: [],
      seatingProActive: false,
      giftCardsActive: false,
      vendorSystemActive: false,
      mobileAppActive: false,
      analyticsActive: false,
      aiWebshopAssistantActive: false,
      hardTicketsActive: false,
      marketingSuiteActive: false,
      experienceProviderActive: false,
      eInvoicePolicy: null,
    }
  }
  const brandingRevision = Number(res.data?.brandingRevision)
  const rawEnt = res.data?.cpEntitlements
  const cpEntitlements = Array.isArray(rawEnt) ? rawEnt.map((x) => String(x)) : []
  const rawPurchased = res.data?.cpPurchasedAddOns
  const cpPurchasedAddOns = Array.isArray(rawPurchased) ? rawPurchased.map((x) => String(x)) : []
  const rawDisabled = res.data?.cpDisabledAddOns
  const cpDisabledAddOns = Array.isArray(rawDisabled) ? rawDisabled.map((x) => String(x)) : []
  const spa = res.data?.seatingProActive
  const seatingProActive =
    (spa === true || activeAddOn(cpEntitlements, cpDisabledAddOns, 'seating_pro')) &&
    !cpDisabledAddOns.includes('seating_pro')
  const gca = res.data?.giftCardsActive
  const giftCardsActive =
    (gca === true || activeAddOn(cpEntitlements, cpDisabledAddOns, 'gift_cards')) &&
    !cpDisabledAddOns.includes('gift_cards')
  const aa = res.data?.analyticsActive
  const maa = res.data?.mobileAppActive
  const awa = res.data?.aiWebshopAssistantActive
  const hta = res.data?.hardTicketsActive
  const msa = res.data?.marketingSuiteActive
  const epa = res.data?.experienceProviderActive
  const vsa = res.data?.vendorSystemActive
  const vendorSystemActive =
    (vsa === true || activeAddOn(cpEntitlements, cpDisabledAddOns, 'vendor_system')) &&
    !cpDisabledAddOns.includes('vendor_system')
  const analyticsActive =
    (aa === true || activeAddOn(cpEntitlements, cpDisabledAddOns, 'analytics')) &&
    !cpDisabledAddOns.includes('analytics')
  const mobileAppActive =
    (maa === true || activeAddOn(cpEntitlements, cpDisabledAddOns, 'mobile_app')) &&
    !cpDisabledAddOns.includes('mobile_app')
  const aiWebshopAssistantActive =
    (awa === true || activeAddOn(cpEntitlements, cpDisabledAddOns, 'ai_webshop_assistant')) &&
    !cpDisabledAddOns.includes('ai_webshop_assistant')
  const hardTicketsActive =
    (hta === true || activeAddOn(cpEntitlements, cpDisabledAddOns, 'hard_tickets')) &&
    !cpDisabledAddOns.includes('hard_tickets')
  const marketingSuiteActive =
    (msa === true || activeAddOn(cpEntitlements, cpDisabledAddOns, 'marketing_suite')) &&
    !cpDisabledAddOns.includes('marketing_suite')
  const experienceProviderActive =
    (epa === true || activeAddOn(cpEntitlements, cpDisabledAddOns, 'experience_provider')) &&
    !cpDisabledAddOns.includes('experience_provider')
  return {
    branding: b as TenantBrandingRecord,
    brandingRevision: Number.isFinite(brandingRevision) ? brandingRevision : 0,
    cpEntitlements,
    cpPurchasedAddOns,
    cpDisabledAddOns,
    seatingProActive,
    giftCardsActive,
    vendorSystemActive,
    mobileAppActive,
    analyticsActive,
    aiWebshopAssistantActive,
    hardTicketsActive,
    marketingSuiteActive,
    experienceProviderActive,
    eInvoicePolicy: asPublicEInvoicePolicy(res.data?.eInvoicePolicy),
  }
}
